import type { CategoryContent } from '../row.js';

/** Maths, Technology and Sport, second chunk. Correct option first; rotated at build. */

export const MORE_MATHS: CategoryContent = {
  msc: [
    [
      'What does the intermediate value theorem require of the function?',
      [
        'Continuity on the closed interval',
        'Differentiability on the open interval',
        'Monotonicity on the closed interval',
        'Boundedness of the derivative',
      ],
      0,
      'Continuity alone. Differentiability is what the mean value theorem needs, and it is the usual confusion between the two.',
    ],
    [
      'A group of prime order p is necessarily what?',
      [
        'Cyclic, and hence isomorphic to the integers modulo p',
        'Abelian but not necessarily cyclic',
        'Simple but not necessarily abelian',
        'Isomorphic to a subgroup of the symmetric group on p letters only',
      ],
      0,
      'Lagrange forces every non-identity element to have order p, so any one of them generates the whole group.',
    ],
    [
      'What does the determinant of a linear map measure geometrically?',
      [
        'The signed factor by which it scales volume',
        'The length of the longest vector in the image of the unit ball',
        'The sum of the squared entries of its matrix representation',
        'The angle between the image and preimage of a basis vector',
      ],
      0,
      'Which is why a zero determinant means the map collapses volume, and why sign flips correspond to orientation reversal.',
    ],
    [
      'In a metric space, what does completeness mean?',
      [
        'Every Cauchy sequence converges to a limit in the space',
        'Every bounded sequence has a convergent subsequence',
        'Every open cover has a finite subcover',
        'The space contains no isolated points',
      ],
      0,
      'The rationals are the standard incomplete example, and the reals are what you get by completing them.',
    ],
    [
      'What is the radius of convergence of the power series for the exponential function?',
      ['Infinite', 'One', 'Zero', 'Equal to the base of the exponential'],
      0,
      'The factorial in the denominator dominates any fixed argument, which is why exp is entire.',
    ],
    [
      'Bayes\' theorem expresses which relationship?',
      [
        'The posterior in terms of the likelihood, the prior and the marginal probability of the data',
        'The joint distribution as the product of independent marginals',
        'The expectation of a sum as the sum of expectations',
        'The variance of a sum in terms of covariances, requiring no assumption of independence at all',
      ],
      0,
      'Its content is trivial and its use is not: everything difficult is in specifying the prior and computing the marginal.',
    ],
    [
      'What does it mean for a matrix to be orthogonal?',
      [
        'Its transpose is its inverse',
        'Its columns are linearly independent',
        'Its eigenvalues are all real and non-negative',
        'It is diagonalisable over the real numbers',
      ],
      0,
      'Determinant plus or minus one follows, and the two signs are rotations and reflections respectively.',
    ],
    [
      'What is the difference between a necessary and a sufficient condition?',
      [
        'A necessary condition must hold for the conclusion; a sufficient condition guarantees it',
        'A necessary condition guarantees the conclusion; a sufficient condition merely permits it',
        'They are logically equivalent formulations',
        'A necessary condition applies to universals and a sufficient one to particulars',
      ],
      0,
      'Elementary and endlessly misused. Both together is an iff, which is what most interesting theorems actually establish.',
    ],
    [
      'What does the pigeonhole principle assert?',
      [
        'If n items occupy fewer than n containers, some container holds more than one',
        'Any finite set can be partitioned into subsets of equal size',
        'A bijection exists between any two infinite countable sets',
        'Any sequence in a bounded set has a convergent subsequence',
      ],
      0,
      'Trivial to state and responsible for some genuinely surprising results, Ramsey theory among them.',
    ],
    [
      'What is a Markov chain\'s defining property?',
      [
        'The next state depends only on the current state',
        'All states are visited with equal long-run frequency',
        'Transition probabilities change deterministically over time',
        'The process returns to its initial state with probability one',
      ],
      0,
      'Memorylessness. Recurrence and stationarity are additional properties a chain may or may not have.',
    ],
  ],
  phd: [
    [
      'What does the spectral theorem for compact self-adjoint operators on Hilbert space give?',
      [
        'A countable orthonormal basis of eigenvectors with eigenvalues accumulating only at zero',
        'A continuous spectrum with no eigenvalues',
        'A finite-dimensional invariant subspace for every such operator',
        'A unitary equivalence to a multiplication operator on a finite measure space',
      ],
      0,
      'Compactness is what forces the discrete spectrum. Drop it and the multiplication-operator picture is what survives.',
    ],
    [
      'What does the Radon-Nikodym theorem provide?',
      [
        'A density function for one measure with respect to another, given absolute continuity',
        'The existence of a product measure on a product space',
        'A decomposition of any measure into discrete and continuous parts',
        'The uniqueness of Lebesgue measure up to scaling',
      ],
      0,
      'It is what makes "probability density" a theorem rather than a convention, and it underwrites conditional expectation.',
    ],
    [
      'What is a martingale, informally?',
      [
        'A process whose conditional expectation of the next value, given the past, equals its current value',
        'A process with independent, identically distributed increments',
        'A process whose variance grows linearly in time, as a Brownian motion with constant diffusion does',
        'A process that returns to its starting value infinitely often',
      ],
      0,
      'The optional stopping theorem is the reason they matter: it turns "fair game" into a computational tool.',
    ],
    [
      'What does Sard\'s theorem state?',
      [
        'The set of critical values of a sufficiently smooth map has measure zero',
        'Every smooth map between manifolds of equal dimension is a local diffeomorphism',
        'A smooth manifold admits a Morse function with distinct critical values',
        'Every continuous map between compact manifolds is homotopic to a smooth one',
      ],
      0,
      'It is what makes "generic" arguments in differential topology legitimate - almost every value is a regular value.',
    ],
    [
      'In algebraic topology, what does the Mayer-Vietoris sequence compute?',
      [
        'The homology of a space from the homologies of two open sets covering it and their intersection',
        'The homotopy groups of a fibration from those of fibre and base',
        'The cohomology of a product space from its factors, through the tensor product of their cohomologies',
        'The Euler characteristic from a cell decomposition',
      ],
      0,
      'The fibration case is the long exact sequence of a fibration, and the product case is Kunneth - three tools routinely conflated.',
    ],
    [
      'What is the significance of the Hahn-Banach theorem?',
      [
        'Bounded linear functionals extend from a subspace to the whole space without increasing norm',
        'Every bounded operator on a Hilbert space attains its norm somewhere on the closed unit ball of that space',
        'Every normed space is isometrically embeddable in a Hilbert space',
        'Every closed bounded convex set in a Banach space is compact',
      ],
      0,
      'It is why duality theory is non-vacuous, and its proof leans on the axiom of choice in the general case.',
    ],
    [
      'What does the central limit theorem require beyond independence and identical distribution?',
      [
        'Finite variance',
        'Finite third moment',
        'A symmetric distribution',
        'A continuous distribution function',
      ],
      0,
      'Without it you land in the stable-law regime instead, which is why heavy-tailed data breaks so much standard practice.',
    ],
    [
      'What is the Galois correspondence for a finite Galois extension?',
      [
        'An inclusion-reversing bijection between subgroups of the Galois group and intermediate fields',
        'An isomorphism between the Galois group and the multiplicative group of the field',
        'A bijection between irreducible polynomials and conjugacy classes',
        'An order-preserving map from subfields to quotient groups',
      ],
      0,
      'Inclusion-reversing is the part that makes solvability by radicals equivalent to solvability of the group.',
    ],
    [
      'What does the Riesz representation theorem say for a Hilbert space?',
      [
        'Every bounded linear functional is an inner product against a unique vector',
        'Every self-adjoint operator has a spectral decomposition',
        'Every closed subspace admits an orthogonal complement',
        'Every orthonormal basis has the same cardinality',
      ],
      0,
      'It is what makes a Hilbert space canonically isomorphic to its own dual, which no general Banach space enjoys.',
    ],
    [
      'What is the P versus NP question, stated precisely?',
      [
        'Whether every problem whose solutions are verifiable in polynomial time is also solvable in polynomial time',
        'Whether every NP-complete problem admits a polynomial-time approximation',
        'Whether non-deterministic machines are strictly more powerful than deterministic ones in any resource',
        'Whether polynomial-time algorithms exist for all problems in PSPACE',
      ],
      0,
      'Verification versus solution. Non-determinism is more powerful for space in a different sense, which makes the machine-power framing a real but distinct question.',
    ],
  ],
  professor: [
    [
      'What does the Atiyah-Singer index theorem relate?',
      [
        'The analytic index of an elliptic operator to a topological invariant of the manifold',
        'The Euler characteristic to the integral of curvature',
        'The signature of a manifold to its intersection form, which is Hirzebruch\'s earlier theorem',
        'The dimension of a moduli space to the genus of a curve',
      ],
      0,
      'Gauss-Bonnet and Riemann-Roch both fall out as special cases, which is a fair measure of its reach.',
    ],
    [
      'What is the Langlands programme, in outline?',
      [
        'A web of conjectural correspondences between automorphic representations and Galois representations',
        'A programme to classify all finite simple groups',
        'A framework for proving the Riemann hypothesis via zeta function symmetries',
        'A conjectural classification of algebraic surfaces up to birational equivalence',
      ],
      0,
      'The proof of Fermat\'s Last Theorem runs through a case of it, which is how a conjectural programme acquired concrete consequences.',
    ],
    [
      'What does the Weil conjectures\' proof by Deligne establish?',
      [
        'Properties of zeta functions of varieties over finite fields',
        'The Riemann hypothesis for the classical zeta function',
        'The Mordell conjecture on rational points of curves',
        'The rationality of the Hodge conjecture for abelian varieties',
      ],
      0,
      'Grothendieck built etale cohomology largely for this, and Deligne finished it in 1974.',
    ],
    [
      'What is the significance of the classification of finite simple groups?',
      [
        'A complete list in eighteen families plus twenty-six sporadic groups, assembled across thousands of pages by many authors',
        'A proof that all finite simple groups are of prime order or alternating',
        'A single unified proof of about two hundred pages, produced once the sporadic groups had been enumerated independently of it',
        'A classification valid only for groups of odd order',
      ],
      0,
      'The odd-order case is Feit-Thompson, an early and enormous ingredient. The second-generation project to shorten the whole thing is still running.',
    ],
    [
      'What does the Poincare-Bendixson theorem constrain?',
      [
        'The possible long-run behaviour of bounded planar flows',
        'The number of limit cycles a polynomial vector field can have',
        'The stability of periodic orbits under small perturbation',
        'The existence of invariant tori in Hamiltonian systems',
      ],
      0,
      'It is why chaos needs three dimensions in continuous time, and why the Lorenz system was such a surprise.',
    ],
    [
      'What is the content of the Ax-Grothendieck theorem?',
      [
        'An injective polynomial map from complex affine space to itself is surjective',
        'Every polynomial map between affine varieties is proper',
        'A bijective morphism of varieties is an isomorphism',
        'Every injective continuous self-map of Euclidean space is a homeomorphism',
      ],
      0,
      'Its standard proof passes through finite fields and model theory, which makes it a favourite example of transfer between apparently distant areas.',
    ],
    [
      'What does the Green-Tao theorem prove?',
      [
        'The primes contain arbitrarily long arithmetic progressions',
        'There are infinitely many prime pairs differing by two',
        'Every sufficiently large even number is a sum of two primes',
        'The gaps between consecutive primes are bounded infinitely often',
      ],
      0,
      '2004, using Szemeredi\'s theorem plus a transference principle. Bounded gaps is Zhang and the polymath work, and a separate achievement.',
    ],
    [
      'What is a topos, and why does it matter?',
      [
        'A category behaving like a category of sheaves, providing a setting in which logic and geometry coincide',
        'A category with all finite limits and colimits but no exponentials',
        'A generalisation of a topological space with no underlying point set',
        'A model of set theory without the axiom of choice, in which every set is constructible from the ordinals alone',
      ],
      0,
      'Internal logic is the payoff: a topos has one, and it need not be classical, which is what makes it a site for constructive mathematics.',
    ],
    [
      'What does the Hodge conjecture assert?',
      [
        'Certain rational cohomology classes on a projective variety are combinations of classes of algebraic subvarieties',
        'Every compact Kahler manifold is projective, so the analytic and the algebraic categories coincide entirely in that setting',
        'The Hodge decomposition exists for any compact complex manifold',
        'Every algebraic cycle is homologous to a smooth one',
      ],
      0,
      'One of the Millennium Problems. The Hodge decomposition itself is a theorem for Kahler manifolds; the conjecture is about which classes are algebraic.',
    ],
    [
      'What does the Banach-Alaoglu theorem state, and why is it useful?',
      [
        'The closed unit ball of a dual space is weak-star compact',
        'The unit ball of any Banach space is weakly compact',
        'Every bounded sequence in a Banach space has a norm-convergent subsequence',
        'The dual of a separable space is separable',
      ],
      0,
      'Weak-star rather than norm compactness is the whole point: it is how variational problems get solutions when no norm-convergent subsequence exists.',
    ],
  ],
};

export const MORE_TECHNOLOGY: CategoryContent = {
  msc: [
    [
      'What does a hash table\'s load factor affect?',
      [
        'The expected number of collisions',
        'The total memory required, independent of collision behaviour',
        'The order in which keys are returned on iteration',
        'The worst-case cost of computing the hash function',
      ],
      0,
      'Which is why implementations resize at a threshold rather than when full, and why the amortised analysis matters.',
    ],
    [
      'What problem does NAT traversal solve?',
      [
        'Establishing a direct connection between hosts behind address translators that block unsolicited inbound packets',
        'Translating between IPv4 and IPv6 address families, so that hosts on either protocol can address one another directly',
        'Assigning private addresses to hosts on a local network',
        'Routing between autonomous systems with overlapping address space',
      ],
      0,
      'STUN discovers the mapping, TURN relays when discovery fails, ICE tries the candidates in order.',
    ],
    [
      'What guarantee does a database transaction\'s durability property give?',
      [
        'A committed transaction survives subsequent crashes',
        'Concurrent transactions cannot observe each other\'s intermediate state',
        'A transaction either applies entirely or not at all',
        'The database never violates a declared constraint',
      ],
      0,
      'The other three are isolation, atomicity and consistency - and durability is the one that turns into an argument about fsync.',
    ],
    [
      'What does public-key infrastructure add to raw public-key cryptography?',
      [
        'A chain of trust binding keys to identities through certificate authorities',
        'Forward secrecy for individual sessions',
        'Resistance to quantum attack on the underlying primitive',
        'A mechanism for symmetric key derivation',
      ],
      0,
      'The binding problem is the hard part: the maths never told you whose key it is, and certificate transparency exists because CAs get that wrong.',
    ],
    [
      'What is the purpose of a write-ahead log?',
      [
        'Recording intended changes durably before applying them, so recovery can replay or undo',
        'Buffering writes in memory to improve throughput',
        'Replicating changes to a secondary for read scaling',
        'Compacting old data to reclaim space, by merging overlapping segments in the background',
      ],
      0,
      'It converts random in-place updates into sequential appends, which is a performance property as much as a correctness one.',
    ],
    [
      'What does content-addressable storage use as the key for an object?',
      [
        'A cryptographic hash of the object\'s contents',
        'A monotonically increasing identifier assigned on write',
        'A path within a hierarchical namespace',
        'A UUID generated by the writing client',
      ],
      0,
      'Deduplication and integrity checking come free; the cost is that updating an object changes its name, which is why Git looks the way it does.',
    ],
    [
      'What does the term "idempotent" mean of an API operation?',
      [
        'Applying it more than once has the same effect as applying it once',
        'It never modifies server state',
        'It returns the same response body on every call',
        'It can be safely executed in parallel with itself',
      ],
      0,
      'Which is what makes retry-on-timeout safe. Safe-and-read-only is a different property, and parallel-safety is a third.',
    ],
    [
      'What does a reverse proxy do that a forward proxy does not?',
      [
        'It stands in front of servers and appears to clients as the origin',
        'It caches responses on behalf of clients',
        'It terminates TLS connections',
        'It rewrites request headers before forwarding',
      ],
      0,
      'Both can cache, terminate TLS and rewrite headers. The direction of the anonymity - whose identity is hidden - is the actual distinction.',
    ],
    [
      'What is the practical significance of a system\'s p99 latency?',
      [
        'It captures tail behaviour that the mean hides',
        'It is the latency exceeded by ninety-nine per cent of requests',
        'It is the maximum latency the system can produce under any load',
        'It equals three standard deviations above the mean for normal workloads',
      ],
      0,
      'A request touching a hundred services hits its p99 about once per request, which is why tail latency compounds rather than averages out.',
    ],
    [
      'What does a semaphore initialised to one behave as?',
      [
        'A mutual exclusion lock, though without ownership semantics',
        'A condition variable with a single waiter',
        'A read-write lock permitting concurrent readers',
        'A barrier synchronising two threads',
      ],
      0,
      'The missing ownership is why a semaphore can be released by a thread that never acquired it - convenient, and a rich source of bugs.',
    ],
  ],
  phd: [
    [
      'What does the Paxos algorithm guarantee, and what does it not?',
      [
        'Safety under asynchrony and failure',
        'Both safety and liveness under full asynchrony',
        'Liveness always, with safety only under a majority quorum',
        'Safety only when all nodes are correct',
      ],
      0,
      'FLP forbids the stronger claim. The duelling-proposers scenario is the concrete way liveness fails.',
    ],
    [
      'What is the difference between snapshot isolation and serialisability?',
      [
        'Snapshot isolation permits write skew',
        'Snapshot isolation permits dirty reads of uncommitted data',
        'Snapshot isolation forbids concurrent reads of the same row',
        'Snapshot isolation cannot support long-running read-only queries',
      ],
      0,
      'The constraint that neither transaction individually violates but both together do is the classic write-skew example.',
    ],
    [
      'What does a log-structured merge tree trade against a B-tree?',
      [
        'Higher write throughput through sequential batching',
        'Higher read throughput, at the cost of write amplification',
        'Stronger consistency guarantees, at the cost of memory use',
        'Lower space usage, at the cost of higher latency variance',
      ],
      0,
      'RocksDB and Cassandra sit on one side, traditional relational indexes on the other, and compaction scheduling is where LSM tuning actually happens.',
    ],
    [
      'What does consistent hashing solve?',
      [
        'Redistribution of only a small fraction of keys when the set of nodes changes',
        'Uniform distribution of keys across a fixed set of nodes',
        'Collision resistance of the hash function under adversarial input',
        'Ordering of keys to support range queries',
      ],
      0,
      'Virtual nodes are what fix the load-imbalance problem the naive ring version has.',
    ],
    [
      'What is the head-of-line blocking problem HTTP/2 failed to solve?',
      [
        'Multiplexed streams still share one TCP connection',
        'Server push could not be disabled by clients',
        'Header compression state had to be maintained per stream',
        'Prioritisation hints were advisory and widely ignored',
      ],
      0,
      'Which is precisely why HTTP/3 moved to QUIC over UDP: per-stream loss recovery needs the transport to know about streams.',
    ],
    [
      'What does the term "exactly-once delivery" actually mean in practice?',
      [
        'At-least-once delivery plus idempotent processing or deduplication - the network guarantee itself is unattainable',
        'A transport-level guarantee provided by TCP retransmission',
        'A property of message brokers with persistent storage',
        'A consequence of totally ordered broadcast, which delivers every message once to every correct process in the same order',
      ],
      0,
      'The impossibility follows from the two-generals problem. Everything marketed as exactly-once is effectively-once at the application boundary.',
    ],
    [
      'What is a vector clock used for?',
      [
        'Detecting causal relationships and concurrency between events across processes',
        'Synchronising physical clocks across a cluster',
        'Ordering events totally, with no ambiguity remaining between concurrent ones',
        'Measuring elapsed time between distributed operations',
      ],
      0,
      'It gives a partial order and can detect concurrency, which a Lamport clock cannot - at the cost of size linear in the number of processes.',
    ],
    [
      'What does speculative execution\'s role in Spectre-class attacks depend on?',
      [
        'Microarchitectural state persisting after mis-speculated work is architecturally discarded',
        'Direct reads of privileged memory by unprivileged instructions',
        'Race conditions in the operating system\'s page table handling',
        'Timing variability in the instruction decoder, which leaks the operands that are currently being decoded',
      ],
      0,
      'Cache state is the classic channel. Meltdown is the different, more direct fault-based variant, and conflating the two is common.',
    ],
    [
      'What problem does a Merkle-tree-based anti-entropy protocol solve efficiently?',
      [
        'Identifying which key ranges differ between two replicas without transferring all keys',
        'Ordering concurrent writes to the same key',
        'Compressing replicated data in transit, so that bandwidth rather than comparison cost dominates the exchange',
        'Detecting Byzantine behaviour by a replica',
      ],
      0,
      'Dynamo popularised it. You descend only the subtrees whose hashes disagree, so cost scales with the difference rather than the dataset.',
    ],
    [
      'What does "tail at scale" identify as the dominant source of latency variability?',
      [
        'Shared-resource contention, background maintenance and queueing, amplified by fan-out',
        'Network propagation delay between data centres',
        'Garbage collection pauses in managed runtimes exclusively',
        'Disk seek latency in the storage tier, which dominates whenever the working set misses cache',
      ],
      0,
      'Dean and Barroso, 2013. Hedged requests and micro-partitioning are the mitigations it proposes, and they are now standard practice.',
    ],
  ],
  professor: [
    [
      'What does the CAP theorem\'s "availability" precisely require?',
      [
        'Every request to a non-failing node receives a non-error response, without any latency bound',
        'The system remains reachable from every client at all times',
        'Requests complete within a specified latency budget',
        'A majority of nodes remain able to accept writes, so a minority partition may legitimately refuse service',
      ],
      0,
      'The absence of a latency bound is why PACELC exists: in practice the trade during normal operation is latency against consistency, not availability.',
    ],
    [
      'What is the significance of linearisable registers being universal?',
      [
        'They are not - consensus cannot be built from read-write registers alone',
        'Any concurrent object can be implemented from registers wait-free',
        'Registers and compare-and-swap have equal consensus number',
        'Consensus is solvable with registers given randomisation only in synchronous systems',
      ],
      0,
      'Herlihy\'s consensus number for registers is one; compare-and-swap is infinite, which is why every real lock-free structure needs a strong primitive.',
    ],
    [
      'What does the FLP result leave open, and how do real systems exploit it?',
      [
        'It forbids deterministic termination under full asynchrony only',
        'It forbids consensus entirely, so real systems only approximate it',
        'It applies only to Byzantine failures, not crashes',
        'It applies only with unbounded numbers of participants',
      ],
      0,
      'Ben-Or\'s randomised protocol and Chandra-Toueg failure detectors are the two classic escape routes, and Raft takes the partial-synchrony one.',
    ],
    [
      'What is the relationship between the calculus of communicating systems and pi-calculus?',
      [
        'Pi-calculus extends process calculus with mobility',
        'Pi-calculus restricts CCS to synchronous communication only',
        'CCS is a typed refinement of the untyped pi-calculus',
        'They are notational variants with identical expressive power',
      ],
      0,
      'Name passing is what makes pi-calculus able to model dynamically reconfiguring systems, which CCS cannot.',
    ],
    [
      'What does homomorphic encryption permit, and what remains its obstacle?',
      [
        'Computation on ciphertext without decryption',
        'Encryption whose key can be rotated without re-encrypting data',
        'Verification of a computation without re-executing it',
        'Search over encrypted data with no information leakage at all',
      ],
      0,
      'Gentry\'s bootstrapping made fully homomorphic schemes possible in 2009; the constant factors are what keep them niche. Verifiable computation is a different primitive.',
    ],
    [
      'What does the "end-to-end argument" in system design recommend?',
      [
        'Functions requiring application knowledge belong at the endpoints, not in the network',
        'Every layer should implement reliability independently',
        'Networks should be stateless in all circumstances, since any state held in transit can be lost',
        'Security must be enforced at the lowest possible layer',
      ],
      0,
      'Saltzer, Reed and Clark, 1984. It is an argument about where knowledge lives, and middleboxes are the continuing counter-pressure.',
    ],
    [
      'What is the practical significance of Rice\'s theorem for static analysis?',
      [
        'All non-trivial semantic properties are undecidable',
        'Static analysis is impossible for languages with dynamic dispatch',
        'Type inference is undecidable for any Turing-complete language',
        'Program equivalence is decidable for terminating programs only',
      ],
      0,
      'Soundness versus completeness is the resulting engineering choice, and every real analyser picks one and reports the other as noise.',
    ],
    [
      'What does a zero-knowledge proof establish?',
      [
        'That a statement is true, revealing nothing beyond its truth',
        'That the prover possesses a secret, revealing a commitment to it',
        'That a computation was performed correctly, given the inputs',
        'That two parties computed a joint function without a trusted third party',
      ],
      0,
      'Joint computation without a trusted third party is secure multiparty computation. Succinct verification is a separate property that SNARKs happen to combine with zero knowledge.',
    ],
    [
      'What is the fundamental limitation of differential privacy as deployed?',
      [
        'The privacy budget accumulates across queries',
        'It cannot be applied to aggregate statistics',
        'It requires a trusted curator in all formulations',
        'It provides no protection against an adversary with auxiliary information',
      ],
      0,
      'Robustness to auxiliary information is its main strength. Local models remove the trusted curator at a large cost in accuracy, and epsilon remains the unresolved governance question.',
    ],
    [
      'What does the "attention is all you need" architecture remove, and at what cost?',
      [
        'Recurrence and convolution',
        'Positional information, at the cost of order-insensitivity',
        'Non-linearity, at the cost of expressive power',
        'Parameter sharing, at the cost of memory footprint',
      ],
      0,
      'Positional encodings restore order explicitly. The quadratic cost is what the long line of efficient-attention work has been attacking ever since.',
    ],
  ],
};

export const MORE_SPORT: CategoryContent = {
  msc: [
    [
      'In Formula One, once qualifying begins the FIA\'s parc fermé regulations restrict teams to what?',
      [
        'A short, pre-declared list of changes such as tyre pressures, front wing angle and worn brake components',
        'Deciding which two drivers are entered for the race weekend',
        'Choosing the order in which their cars leave the pit lane during the session',
        'Whether team radio communication with drivers is permitted during the session, and what restrictions the FIA imposes on message content',
      ],
      0,
      'Parc fermé locks the car\'s setup from qualifying through race day. Only a short FIA-approved list of changes is allowed: tyre pressures, front wing angle, worn brake components, and fuel. Larger changes incur a pit-lane start penalty rather than the earned grid position.',
    ],
    [
      'In athletics, what does a wind-assisted mark mean?',
      [
        'A performance with tailwind above 2.0 metres per second, ineligible for records',
        'A performance set at altitude above 1000 metres',
        'A performance in which pacing lights were used',
        'A performance recorded with manual rather than electronic timing',
      ],
      0,
      'It applies to sprints up to 200 metres, the long jump and the triple jump - events where the assistance is measurable in the run-up.',
    ],
    [
      'What is the offside law in football conditioned on?',
      [
        'Position at the moment the ball is played, relative to the second-last defender and the ball',
        'Position at the moment the ball is received by the attacker',
        'Whether the attacker interferes with play, regardless of position',
        'Position relative to the last defender only, with the position of the ball disregarded entirely by the assistant',
      ],
      0,
      'The timing is what makes it hard to officiate, and it is why semi-automated systems focus on the frame of the pass.',
    ],
    [
      'In rowing, what does "rating" refer to?',
      [
        'Strokes per minute',
        'The crew\'s handicap allowance in a staggered start',
        'The ratio of drive time to recovery time',
        'The blade\'s angle of entry at the catch',
      ],
      0,
      'The drive-to-recovery ratio is the "ratio", which is a separate and frequently confused term.',
    ],
    [
      'What does a golf handicap system aim to do?',
      [
        'Equalise competition by adjusting scores according to demonstrated ability',
        'Rank players internationally by tournament performance',
        'Set the tee positions appropriate to a player\'s driving distance',
        'Limit the number of clubs a player may carry',
      ],
      0,
      'The World Handicap System of 2020 unified six regional systems, and course and slope rating are what make a handicap portable.',
    ],
    [
      'What is a "no-ball" penalty in limited-overs cricket?',
      [
        'An extra run plus a free hit',
        'Two extra runs with the delivery counting toward the over',
        'An extra run only, with the batter unable to be dismissed',
        'A five-run penalty awarded to the batting side',
      ],
      0,
      'The free hit was introduced to make the deterrent bite, which is why the front-foot no-ball became worth reviewing automatically.',
    ],
    [
      'What does VO2 max measure?',
      [
        'The maximum rate of oxygen uptake during incremental exercise to exhaustion',
        'The maximum volume of air moved per breath at peak effort',
        'The oxygen debt accumulated during anaerobic work',
        'The efficiency with which oxygen is converted to mechanical work',
      ],
      0,
      'The efficiency measure is running or cycling economy, and among trained athletes it discriminates performance better than VO2 max does.',
    ],
    [
      'In tennis, what does a tiebreak at six-all require to win?',
      [
        'Seven points with a margin of at least two',
        'Seven points regardless of margin',
        'Six points with a margin of at least two',
        'Ten points with a margin of at least two',
      ],
      0,
      'A standard tiebreak needs seven points with a margin of two. The ten-point version is a separate rule, the match tiebreak, used in place of a full final set in some formats such as doubles.',
    ],
    [
      'What is the "Bosman ruling" significant for?',
      [
        'Establishing free transfer at contract expiry within the EU, and striking down nationality quotas',
        'Establishing a salary cap in European club competition, enforced by the governing body across all member leagues',
        'Permitting third-party ownership of player registrations',
        'Requiring clubs to release players for international duty',
      ],
      0,
      '1995, European Court of Justice. It transformed the economics of European football more than any competition-format change.',
    ],
    [
      'In Formula One, what does a "delta time" refer to?',
      [
        'A target time a driver must not beat',
        'The difference between a driver\'s best and average lap',
        'The time lost by a pit stop relative to staying out',
        'The gap between qualifying and race pace',
      ],
      0,
      'It exists so that drivers cannot gain by speeding through a hazard, and it is enforced sector by sector.',
    ],
  ],
  phd: [
    [
      'Under rugby union\'s \'50:22\' law, first trialled by World Rugby in 2021, when does the kicking team win the throw-in at the resulting lineout?',
      [
        'A kick from inside the kicking team\'s own half that bounces in the field of play before going out inside the opposition\'s 22',
        'A kick from anywhere on the pitch that is caught cleanly inside the opposition\'s 22 without bouncing',
        'A kick from inside the opposition\'s own half that bounces before going out inside the opposition\'s 22',
        'A kick that bounces first inside the opposition\'s 22 after crossing the opposition\'s try line and being played back into the field of play',
      ],
      0,
      'A kick from the kicking team\'s own half that bounces in the field of play before going out inside the opposition\'s 22 results in the kicking team keeping the throw-in, normally conceded on touch-kicks.',
    ],
    [
      'In 2021, UEFA abolished which long-standing tie-breaking rule for two-legged knockout ties in its European club competitions?',
      [
        'The away goals rule, under which a goal scored away from home had broken a tie level on aggregate since 1965',
        'The requirement for a replay following a semi-final drawn on aggregate after extra time',
        'The golden goal rule deciding extra time in the second leg',
        'The seeding advantage granted to the higher-ranked club in the event of a tied aggregate score',
      ],
      0,
      'Introduced in 1965/66 to reward attacking play away from home, home advantage had shrunk enough that the rule incentivized away teams to defend narrow leads rather than attack—the opposite of its intent. UEFA scrapped it for the 2021/22 season.',
    ],
    [
      'What is the principal statistical objection to using penalty conversion rate to rank takers?',
      [
        'Sample sizes are tiny, so observed rates are dominated by noise and regress heavily to the mean',
        'Conversion rate is not measurable from match data, since attempts are recorded inconsistently across leagues',
        'Goalkeepers\' abilities are not independent across attempts',
        'Penalties are awarded non-randomly with respect to team strength',
      ],
      0,
      'A career of forty penalties supports almost no discrimination between plausible true rates - the classic small-sample trap in sports analytics.',
    ],
    [
      'What does the Duckworth-Lewis-Stern method model, in structure?',
      [
        'Remaining scoring resources as a function jointly of overs and wickets remaining',
        'Runs as a linear function of overs remaining only',
        'The probability of victory from the current match state',
        'Expected wickets lost per over at each stage of an innings',
      ],
      0,
      'The two-dimensional resource table is why the revised target is rarely pro rata, and the Stern revision addressed high-scoring modern totals.',
    ],
    [
      'In biomechanics, what does the stretch-shortening cycle describe?',
      [
        'Enhanced concentric force following rapid eccentric loading',
        'The reciprocal inhibition of antagonist muscles during rapid movement',
        'The length-tension relationship of a muscle at fixed activation',
        'The trade-off between force and velocity in concentric contraction',
      ],
      0,
      'It is why a countermovement jump beats a squat jump, and why plyometric training is prescribed at all.',
    ],
    [
      'What is the "marginal gains" methodology\'s main methodological weakness?',
      [
        'Individually undetectable effects are aggregated without independent evidence for most of them',
        'It requires equipment unavailable to most athletes',
        'It assumes all interventions have equal effect size',
        'It cannot be applied to team sports, where individual contributions are not separable from the collective result',
      ],
      0,
      'Post hoc attribution to a bundle of interventions after a successful programme is not evidence for any particular member of the bundle.',
    ],
    [
      'What does "relative age effect" refer to in talent identification?',
      [
        'Over-representation of athletes born early in a selection year',
        'The tendency of older athletes to outperform younger ones at elite level',
        'The age at which peak performance occurs in a given sport',
        'The advantage of early specialisation over late diversification',
      ],
      0,
      'It is self-reinforcing: early selection gives better coaching, which justifies the next selection - the underdog literature argues the disadvantaged group performs better long-term.',
    ],
    [
      'What is the physiological basis of altitude training\'s "live high, train low" protocol?',
      [
        'Hypoxic residence stimulates erythropoiesis while low-altitude training preserves absolute training intensity',
        'High-altitude training improves running economy through altered biomechanics',
        'Hypoxia increases muscle capillary density more than sea-level training',
        'Low-altitude sleep improves recovery from high-altitude work, so the adaptation is preserved while fatigue is cleared',
      ],
      0,
      'Levine and Stray-Gundersen. The design exists because the naive protocol sacrifices intensity to get the adaptation.',
    ],
    [
      'What does the Elo system\'s K-factor control?',
      [
        'How responsive ratings are to each result, trading noise sensitivity against speed of adjustment',
        'The expected score for a given rating difference, through the logistic function mapping points to probability',
        'The minimum number of games before a rating is provisional',
        'The rating floor below which a player cannot fall',
      ],
      0,
      'FIDE varies it by rating and experience, which is a direct admission that one number cannot serve every population.',
    ],
    [
      'In sports economics, what does the "uncertainty of outcome" hypothesis predict?',
      [
        'Demand rises with competitive balance',
        'Revenue rises with the number of matches played per season',
        'Attendance depends primarily on ticket price elasticity',
        'Broadcast value is maximised by concentrating talent in a few clubs',
      ],
      0,
      'The empirical support is mixed and sport-specific - superstar effects can outweigh balance, which is awkward for the policy conclusions usually drawn from it.',
    ],
  ],
  professor: [
    [
      'UEFA\'s squad cost ratio, phased in as part of the Financial Sustainability Regulations that replaced Financial Fair Play, caps a club\'s spending on wages, transfer amortisation and agent fees at what share of relevant revenue once fully phased in for the 2025/26 season?',
      [
        '70%',
        '90%',
        '80%',
        '60%',
      ],
      0,
      'UEFA introduced the ratio as an affordability test after break-even rules proved easy to circumvent. It caps wages, transfer amortization and agent fees at 70% of football-related revenue, phased gently from 90% in 2023/24 to its permanent 70% ceiling from 2025/26.',
    ],
    [
      'What did the European Court of Justice establish in its 2006 ruling in Meca-Medina and Majcen v Commission?',
      [
        'That sporting rules, including anti-doping regulations, are not automatically exempt from EU competition law merely because they are sport-specific',
        'That national anti-doping agencies must impose identical suspension lengths across every EU member state for the same offence',
        'That doping sanctions imposed by international federations cannot be challenged before national courts',
        'That the \'specificity of sport\' doctrine exempts federation disciplinary and eligibility rules entirely from the EU\'s single-market and competition-law obligations, regardless of their economic effect on athletes',
      ],
      0,
      'Two swimmers challenged a doping ban, arguing anti-doping rules were purely sporting and beyond EU law. The Court ruled that a rule\'s sporting character does not automatically exempt it from competition law if it has genuine economic effects on athletes\' work.',
    ],
    [
      'In 2014 the UCI fundamentally changed the technical rules governing cycling\'s Hour Record. What was the change?',
      [
        'Modern aerodynamic time-trial equipment was allowed again, ending the seventeen-year restriction to Eddy Merckx\'s 1972 setup',
        'The record was split permanently into separate \'standard equipment\' and \'superhuman\' categories judged on power output alone',
        'Indoor velodrome attempts were banned in favour of outdoor road-circuit attempts only',
        'A minimum attempt duration of forty-five minutes, together with a requirement that attempts be independently ratified by two continental federations, was introduced to disqualify short, high-intensity efforts',
      ],
      0,
      'The UCI froze the record to Merckx-era 1972 equipment in 1997 to manage technological arms races. That restriction lasted seventeen years until 2014, when the UCI reversed course and allowed modern time-trial technology, prompting attempts with dramatically higher distances.',
    ],
    [
      'What does the biological-passport approach to anti-doping rest on?',
      [
        'Longitudinal within-athlete monitoring of biomarkers, flagging deviations from an individual baseline',
        'Direct detection of prohibited substances at lower thresholds',
        'Population reference ranges applied to each test result, flagging any value outside the distribution for that sex and sport',
        'Retrospective analysis of stored samples with newer assays',
      ],
      0,
      'It infers doping from physiological consequence rather than from the substance - and the resulting evidentiary standard has been litigated hard.',
    ],
    [
      'What is the central difficulty in regulating transgender and DSD participation in elite sport?',
      [
        'Balancing inclusion against a contested and event-specific evidence base on retained performance advantage',
        'The absence of any measurable performance differences between groups, once event and training history are controlled',
        'The unavailability of testosterone measurement in competition conditions',
        'A lack of legal jurisdiction over eligibility in international federations',
      ],
      0,
      'The Semenya litigation is where the evidentiary and legal questions met, and the science genuinely differs by event and by measure.',
    ],
    [
      'What does research on "tactical periodisation" claim, and what is the evidence problem?',
      [
        'Training should integrate tactical, physical and psychological dimensions',
        'Physical and tactical training should be strictly separated in the weekly cycle',
        'Load should follow a fixed sinusoidal pattern across a season',
        'Small-sided games are sufficient for all conditioning needs',
      ],
      0,
      'Its practitioners treat holism as a premise, which makes component-wise randomisation almost definitionally impossible.',
    ],
    [
      'What does the literature on "competitive balance" measure, and why is comparison across leagues hard?',
      [
        'Dispersion of win percentages',
        'The number of distinct champions over a fixed period only',
        'Revenue disparity between the largest and smallest clubs',
        'The correlation between payroll and final league position',
      ],
      0,
      'The idealised standard deviation normalises for schedule length, which is why an eighty-two-game and a thirty-eight-game league can be compared at all.',
    ],
    [
      'What does the "referee bias" literature identify most robustly?',
      [
        'Home advantage in discretionary decisions, attenuated when crowds are absent',
        'Systematic bias toward higher-ranked teams independent of crowd',
        'Bias attributable to referee nationality in international fixtures only',
        'No measurable bias once decision difficulty is controlled',
      ],
      0,
      'The behind-closed-doors matches of 2020 were an unplanned natural experiment, and the crowd effect on discretionary calls largely survived it.',
    ],
    [
      'What is the analytical problem with "clutch performance" as a construct?',
      [
        'Once selection and sample size are handled, persistent individual clutch ability is very hard to distinguish from noise',
        'High-pressure situations cannot be operationally defined, since win probability and scoreline both change continuously through a match',
        'Performance data are unavailable for the relevant moments',
        'It has been shown to be entirely absent in every sport studied',
      ],
      0,
      'The honest position is that the effect, if present, is small relative to the noise in any career-length sample - which is not the same as showing it absent.',
    ],
    [
      'What does the economics of hosting mega-events generally conclude?',
      [
        'Ex-ante projections systematically exceed ex-post measured benefits',
        'Hosting reliably produces long-run tourism and infrastructure gains',
        'Effects are neutral in developed and positive in developing economies',
        'Benefits accrue mainly through export promotion effects',
      ],
      0,
      'Baade and Matheson\'s work is the standard citation. The most defensible remaining claim is an intangible feel-good effect, which is real and hard to price.',
    ],
  ],
};
